import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  AlertTriangle, 
  Search,
  FileText,
  Download,
} from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ContractTemplate, TemplateType } from '@/types/business-opportunities';
import { getContractTemplates } from '@/services/business-opportunities';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/utils';
import { sanitizeTemplateHTML } from '@/utils/htmlSanitizer';

// Template parcial para listar, sem campos obrigatórios do BaseEntity
type DisplayableTemplate = Omit<Partial<ContractTemplate>, 'created_by' | 'updated_by'> & {
  id: string;
  name: string;
  description: string;
  template_type: TemplateType;
  html_content?: string;
  docx_file_url?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

const ContractTemplates: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [templates, setTemplates] = useState<DisplayableTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [templateTypeFilter, setTemplateTypeFilter] = useState<string>('_all');
  const [statusFilter, setStatusFilter] = useState<string>('_all');
  
  // Modais
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<DisplayableTemplate | null>(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [signedDownloadUrl, setSignedDownloadUrl] = useState<string>('');
  
  // Tipos de templates disponíveis (de acordo com o enum TemplateType)
  const templateTypes = [
    { value: 'services' as TemplateType, label: 'Serviço' },
    { value: 'products' as TemplateType, label: 'Produto' },
    { value: 'licensing' as TemplateType, label: 'Licenciamento' },
    { value: 'consultancy' as TemplateType, label: 'Consultoria' },
    { value: 'subscription' as TemplateType, label: 'Assinatura' }
  ];
  
  useEffect(() => {
    fetchTemplates();
  }, []);
  
  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      
      const data = await getContractTemplates();
      
      if (data) {
        // Convert the API response to our local template type
        const templates: DisplayableTemplate[] = data.map(item => ({
          id: item.id,
          name: item.name,
          description: item.description || '',
          template_type: item.template_type as TemplateType,
          html_content: item.html_content,
          docx_file_url: item.docx_file_url,
          is_active: item.is_active,
          created_at: item.created_at,
          updated_at: item.updated_at
        }));
        
        setTemplates(templates);
      }
    } catch (error) {
      console.error('Erro ao buscar templates de contrato:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os templates de contrato.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteClick = (template: DisplayableTemplate) => {
    setSelectedTemplate(template);
    setShowDeleteDialog(true);
  };
  
  const handleViewClick = (template: DisplayableTemplate) => {
    setSelectedTemplate(template);
    setShowViewDialog(true);
  };
  
  const handleEditClick = (template: DisplayableTemplate) => {
    navigate(`/contract-templates/${template.id}`);
  };
  
  const handleCreateTemplate = () => {
    navigate('/contract-templates/new');
  };
  
  const confirmDelete = async () => {
    if (!selectedTemplate) return;
    
    try {
      // Usando supabase diretamente pois ainda não temos a função no serviço
      const { error } = await supabase
        .from('contract_templates')
        .delete()
        .eq('id', selectedTemplate.id);
        
      if (error) throw error;
      
      // Atualizar a lista local
      setTemplates(templates.filter(t => t.id !== selectedTemplate.id));
      
      toast({
        title: 'Template excluído',
        description: 'O template de contrato foi excluído com sucesso.',
      });
      
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível excluir o template de contrato.',
        variant: 'destructive',
      });
    }
  };
  
  // Filtragem de templates
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = searchQuery === '' || 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesType = templateTypeFilter === '_all' || templateTypeFilter === template.template_type;
    const matchesStatus = statusFilter === '_all' || 
      (statusFilter === 'active' && template.is_active) ||
      (statusFilter === 'inactive' && !template.is_active);
      
    return matchesSearch && matchesType && matchesStatus;
  });
  
  // Obter o rótulo para um tipo de template
  const getTemplateTypeLabel = (type: string) => {
    const templateType = templateTypes.find(t => t.value === type);
    return templateType ? templateType.label : type;
  };
  
  // Adicionar função para download do arquivo de template
  const handleDownloadTemplate = async (template: DisplayableTemplate) => {
    try {
      if (!template.docx_file_url) {
        toast({
          title: "Erro",
          description: "Nenhum arquivo de template disponível para download.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Download iniciado",
        description: "Preparando o arquivo para download...",
      });

      const { data, error } = await supabase
        .storage
        .from('contracts')
        .download(`templates/${template.id}.docx`);

      if (error) {
        console.error('Erro ao baixar arquivo:', error);
        toast({
          title: "Erro no download",
          description: `Não foi possível baixar o arquivo: ${error.message}`,
          variant: "destructive",
        });
        return;
      }

      // Criar um blob a partir dos dados e gerar uma URL para o download
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = URL.createObjectURL(blob);

      // Criar um link de download e executá-lo
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_${template.name.replace(/\s+/g, '_')}.docx`;
      document.body.appendChild(a);
      a.click();
      
      // Limpar recursos
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Download concluído",
        description: "O arquivo foi baixado com sucesso.",
      });
    } catch (err) {
      console.error('Erro ao processar download:', err);
      toast({
        title: "Erro no download",
        description: "Ocorreu um erro inesperado durante o download.",
        variant: "destructive",
      });
    }
  };
  
  const handlePreviewTemplate = async (template: DisplayableTemplate) => {
    try {
      if (!template.docx_file_url) {
        toast({
          title: "Preview indisponível",
          description: "Não há arquivo de template disponível para visualizar.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Preparando preview",
        description: "Configurando visualização do documento...",
      });

      // Criar uma URL assinada com validade de 1 hora para acessar o arquivo
      const { data: signedUrlData, error: signedUrlError } = await supabase
        .storage
        .from('contracts')
        .createSignedUrl(`templates/${template.id}.docx`, 60 * 60);

      if (signedUrlError) {
        console.error('Erro ao gerar URL assinada:', signedUrlError);
        toast({
          title: "Erro no preview",
          description: "Não foi possível gerar uma URL de acesso para o documento.",
          variant: "destructive",
        });
        return;
      }

      // Usar o serviço do Office Online Viewer com a URL assinada
      const encodedUrl = encodeURIComponent(signedUrlData.signedUrl);
      const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
      
      setPreviewUrl(viewerUrl);
      setSignedDownloadUrl(signedUrlData.signedUrl);
      setShowPreviewDialog(true);

    } catch (err) {
      console.error('Erro ao preparar preview:', err);
      toast({
        title: "Erro no preview",
        description: "Não foi possível preparar a visualização do documento.",
        variant: "destructive",
      });
    }
  };
  
  if (isLoading) {
    return (
      <div className="w-full max-w-full px-4 py-6 flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  return (
    <div className="w-full max-w-full px-4 py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-extrabold">Templates de Contrato</h1>
          <p className="text-muted-foreground">
            Gerencie os templates utilizados para gerar contratos.
          </p>
        </div>
        
        <Button onClick={handleCreateTemplate}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Template
        </Button>
      </div>
      
      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative w-full md:w-1/3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
              <Input
                placeholder="Buscar templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="w-full md:w-1/5">
              <Select value={templateTypeFilter} onValueChange={setTemplateTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tipo de Template" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos os tipos</SelectItem>
                  {templateTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-full md:w-1/5">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos</SelectItem>
                  <SelectItem value="active">Ativos</SelectItem>
                  <SelectItem value="inactive">Inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button variant="outline" className="md:ml-auto" onClick={() => {
              setSearchQuery('');
              setTemplateTypeFilter('_all');
              setStatusFilter('_all');
            }}>
              Limpar Filtros
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Tabela de Templates */}
      <Card>
        <CardHeader>
          <CardTitle>Templates Disponíveis</CardTitle>
          <CardDescription>
            {filteredTemplates.length} {filteredTemplates.length === 1 ? 'template encontrado' : 'templates encontrados'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-extrabold">Nenhum template encontrado</h3>
              <p className="mt-1 text-sm text-gray-500">
                {templates.length === 0 
                  ? 'Comece criando seu primeiro template de contrato.'
                  : 'Tente ajustar os filtros ou a busca para encontrar o que procura.'}
              </p>
              {templates.length === 0 && (
                <Button onClick={handleCreateTemplate} className="mt-4">
                  <Plus className="mr-2 h-4 w-4" />
                  Criar Template
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead>Atualizado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div className="font-extrabold">{template.name}</div>
                      <div className="text-sm text-muted-foreground line-clamp-2">
                        {template.description}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getTemplateTypeLabel(template.template_type)}
                    </TableCell>
                    <TableCell>
                      {template.is_active ? (
                        <Badge variant="success">Ativo</Badge>
                      ) : (
                        <Badge variant="secondary">Inativo</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(template.created_at)}
                    </TableCell>
                    <TableCell>
                      {formatDate(template.updated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button variant="ghost" size="icon" onClick={() => handleViewClick(template)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEditClick(template)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" 
                          onClick={() => handleDeleteClick(template)}
                          disabled={template.is_active} // Não permitir excluir templates ativos
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Modal de visualização */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[1200px]">
          <DialogHeader>
            <DialogTitle>Visualizar Template</DialogTitle>
            <DialogDescription>
              Detalhes do template de contrato.
            </DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <h3 className="text-lg font-extrabold">{selectedTemplate.name}</h3>
                <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <p>{getTemplateTypeLabel(selectedTemplate.template_type)}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <p>{selectedTemplate.is_active ? 'Ativo' : 'Inativo'}</p>
                </div>
                <div>
                  <Label>Criado em</Label>
                  <p>{formatDate(selectedTemplate.created_at)}</p>
                </div>
                <div>
                  <Label>Atualizado em</Label>
                  <p>{formatDate(selectedTemplate.updated_at)}</p>
                </div>
              </div>
              
              <div>
                <Label>Conteúdo do Template</Label>
                {selectedTemplate.docx_file_url ? (
                  <div className="mt-2 p-4 border rounded-md bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-blue-500" />
                      <span>Arquivo Word (.docx)</span>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDownloadTemplate(selectedTemplate)}
                      className="flex items-center"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Baixar
                    </Button>
                  </div>
                ) : selectedTemplate.html_content ? (
                  <div className="mt-2 p-4 border rounded-md bg-gray-50 text-sm">
                    <div dangerouslySetInnerHTML={{ __html: sanitizeTemplateHTML(selectedTemplate.html_content) }} />
                  </div>
                ) : (
                  <div className="mt-2 p-4 border rounded-md bg-gray-50 text-sm text-muted-foreground">
                    Nenhum conteúdo disponível
                  </div>
                )}
              </div>

              {selectedTemplate.docx_file_url && (
                <div className="mt-4">
                  <Label>Arquivo do Template</Label>
                  <div className="flex items-center mt-2 p-2 border rounded-md">
                    <FileText className="h-5 w-5 mr-2" />
                    <span className="text-sm mr-auto">Template DOCX</span>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePreviewTemplate(selectedTemplate)}
                        className="flex items-center"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Visualizar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadTemplate(selectedTemplate)}
                        className="flex items-center"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Baixar
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Conteúdo do template HTML (se existir) */}
              {selectedTemplate.html_content && (
                <div className="mt-4">
                  <Label>Conteúdo HTML do Template</Label>
                  <div className="mt-2 p-4 border rounded-md bg-gray-50 max-h-[400px] overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">{selectedTemplate.html_content}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Fechar
            </Button>
            {selectedTemplate && (
              <Button onClick={() => {
                setShowViewDialog(false);
                handleEditClick(selectedTemplate);
              }}>
                Editar Template
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-[90vw] md:max-w-[80vw] lg:max-w-[800px] w-full h-[80vh] max-h-[800px] flex flex-col">
          <DialogHeader>
            <DialogTitle>Visualizar Template</DialogTitle>
            <DialogDescription>
              Prévia do documento de template
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden">
            <iframe
              src={previewUrl}
              style={{ width: '100%', height: '100%', minHeight: '500px', border: 'none' }}
              frameBorder="0"
              allowFullScreen
            ></iframe>
          </div>
          
          <DialogFooter className="mt-4">
            {selectedTemplate && (
              <Button
                onClick={() => window.open(signedDownloadUrl || selectedTemplate.docx_file_url, '_blank')}
                variant="outline"
                className="flex items-center"
              >
                <Download className="mr-2 h-4 w-4" />
                Baixar arquivo original
              </Button>
            )}
            <Button
              onClick={() => window.open(previewUrl, '_blank')}
              variant="outline"
              className="flex items-center"
            >
              <Eye className="mr-2 h-4 w-4" />
              Abrir em nova janela
            </Button>
            <Button
              onClick={() => setShowPreviewDialog(false)}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal de confirmação de exclusão */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir este template de contrato?
            </DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="py-4">
              <div className="flex items-center space-x-2 text-amber-600 mb-4">
                <AlertTriangle className="h-5 w-5" />
                <p className="font-extrabold">Esta ação não pode ser desfeita.</p>
              </div>
              
              <p>
                Você está prestes a excluir o template: <strong>{selectedTemplate.name}</strong>
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractTemplates; 